// React & React Ecosystem
import React, { useContext, useEffect, useState, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

// Third-party Libraries
import {
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
} from 'react-accessible-accordion';

// Material UI
import Button from '@mui/material/Button';

// Material UI Icons
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import CurrencyExchangeOutlinedIcon from '@mui/icons-material/CurrencyExchangeOutlined';
import DoneIcon from '@mui/icons-material/Done';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import LocalAtmOutlinedIcon from '@mui/icons-material/LocalAtmOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';

// Context
import { ActiveChatContext } from '../../context/ActiveChatContext';
import { GlobalHandlersContext } from '../../context/GlobalHandlersContext';

// Modules
import { getData } from '../../modules/fetch';
import { formatPrice } from '../../modules/price';
import renderDate from '../../modules/renderDate';
import { toTitleCase } from '../../modules/string';

// Styles
import '../../components/Accordion/styles.css';
import {
  AccordionTitle,
  Buttons,
  Container,
  Img,
  ImgContainer,
  Info,
  Item,
  ItemClick,
  Loading,
  Order,
  OrderDetails,
  PerItem,
  Price,
  SKU,
  StyledAccordion,
  StyledAccordionItemPanel,
  SubItem,
  Title,
} from './style';

const CustomerOrders = () => {
  const [activeChat] = useContext(ActiveChatContext);
  const [orders, setOrders] = useState<any[]>([]);
  const [globalHandlers] = useContext<any>(GlobalHandlersContext);
  const [panels, setPanels] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const currentCustomerIdRef = useRef<string | null>(null);

  const loadOrders = (pageNumber: number) => {
    if (isLoading || !hasMore) return;
    if (!activeChat?._id?.$oid) return;

    const customerId = activeChat._id.$oid;
    setIsLoading(true);

    getData(`/${activeChat.entity}s/orders/${customerId}/${pageNumber}`)
      .then((response) => {
        if (customerId !== currentCustomerIdRef.current) return;
        if (response?.data && response?.meta?.pagination) {
          setOrders((prev) => [...prev, ...response.data]);
          const pagination = response.meta.pagination;
          setHasMore(pagination.page < pagination.max_page);
          setPage(pageNumber);
        } else if (response?.data) {
          if (response.data.length === 0) {
            setHasMore(false);
          } else {
            setOrders((prev) => [...prev, ...response.data]);
            setHasMore(false);
          }
          setPage(pageNumber);
        }
      })
      .catch((err) => {
        if (customerId !== currentCustomerIdRef.current) return;
        console.error(err);
      })
      .finally(() => {
        if (customerId === currentCustomerIdRef.current) {
          setIsLoading(false);
        }
      });
  };

  useEffect(() => {
    if (!activeChat?._id?.$oid) return;

    const customerId = activeChat._id.$oid;
    currentCustomerIdRef.current = customerId;
    setPage(1);
    setOrders([]);
    setHasMore(true);
    setIsLoading(true);

    getData(`/${activeChat.entity}s/orders/${customerId}/1`)
      .then((response) => {
        if (customerId !== currentCustomerIdRef.current) return;
        if (response?.data && response?.meta?.pagination) {
          setOrders(response.data);
          const pagination = response.meta.pagination;
          setHasMore(pagination.page < pagination.max_page);
          setPage(1);
        } else if (response?.data) {
          if (response.data.length === 0) {
            setHasMore(false);
          } else {
            setOrders(response.data);
            setHasMore(false);
          }
          setPage(1);
        }
      })
      .catch((err) => {
        if (customerId !== currentCustomerIdRef.current) return;
        console.error(err);
      })
      .finally(() => {
        if (customerId === currentCustomerIdRef.current) {
          setIsLoading(false);
        }
      });

    return () => {
      currentCustomerIdRef.current = null;
    };
  }, [activeChat?._id?.$oid, activeChat?.entity]);

  const handleSend = (id: string) => {
    globalHandlers.handleMessageSend('', 'order', id);
  };

  const handleOpenPopupInfo = (group: string) => () => {
    setPanels({ ...panels, [group]: !panels?.[group] });
  };

  const getFormattedOrderDate = (order: any) => {
    const dateValue = order?.orderCreatedAt?.date ?? order?.orderDate?.date;

    if (order?.provider === 'lazada') {
      return dateValue || 'n/a';
    }

    return renderDate(dateValue, order?.store?.timezone?.name) || 'n/a';
  };

  const renderOrder = (order: any, index: number) => {
    return (
      <StyledAccordion allowZeroExpanded key={`order-item-${index}`}>
        <AccordionItem>
          <AccordionItemHeading>
            <AccordionItemButton>
              <AccordionTitle>
                <h5>Order: {order?.orderId}</h5>
              </AccordionTitle>
            </AccordionItemButton>
          </AccordionItemHeading>
          <StyledAccordionItemPanel>
            {order?.orderItemsSummary?.map((item: any, i: number) => {
              const orderItem = order?.orderItems?.find(
                (good: any) => good.orderItemId === (item?.orderItemId || item?.id),
              );
              return (
                <Order key={`order-orderItems-${i}`}>
                  <ImgContainer>
                    <Img
                      src={item?.imageUrl || item?.image || orderItem?.imageUrl || orderItem?.image}
                      alt={item?.name || orderItem?.name || ''}
                    />
                  </ImgContainer>
                  <Info>
                    <Title>{item?.name || orderItem?.name}</Title>
                    <SKU>
                      <FormattedMessage
                        id="messages.customerOrders.accordion.sku"
                        defaultMessage="SKU: "
                      />
                      <br />
                      {item?.sku || item?.skuId || 'n/a'}
                    </SKU>
                    <Price>
                      <PerItem>
                        {formatPrice(
                          order?.store?.countryCode,
                          order?.currency,
                          orderItem?.price || item?.price,
                        )}{' '}
                        x {item?.quantity || item?.amount || 1}
                      </PerItem>
                    </Price>
                  </Info>
                </Order>
              );
            })}
            <OrderDetails>
              <Item>
                <div>
                  <LocalAtmOutlinedIcon />
                  <strong>
                    <FormattedMessage
                      id="messages.customerOrders.details.total"
                      defaultMessage="Total"
                    />
                  </strong>
                </div>
                <div>
                  <strong>
                    {formatPrice(order?.store?.countryCode, order?.currency, order?.price)}
                  </strong>
                </div>
              </Item>
              <Item>
                <div>
                  <CreditCardOutlinedIcon />
                  <FormattedMessage
                    id="messages.customerOrders.details.paymentMethod"
                    defaultMessage="Payment Method"
                  />
                </div>
                <div>{order?.paymentMethod ? toTitleCase(order?.paymentMethod) : 'n/a'}</div>
              </Item>
              <Item>
                <div>
                  <DoneIcon />
                  <FormattedMessage
                    id="messages.customerOrders.details.status"
                    defaultMessage="Order Status"
                  />
                </div>
                <div>{toTitleCase(order?.status || '')}</div>
              </Item>
              <Item>
                <div>
                  <CalendarTodayIcon />
                  <FormattedMessage
                    id="messages.customerOrders.details.created"
                    defaultMessage="Created"
                  />
                </div>
                <div>{getFormattedOrderDate(order)}</div>
              </Item>
              <Item>
                <div>
                  <LocalShippingOutlinedIcon />
                  <FormattedMessage
                    id="messages.customerOrders.details.shippingFee"
                    defaultMessage="Shipping Fee"
                  />
                </div>
                <div>
                  {formatPrice(order?.store?.countryCode, order?.currency, order?.shippingFee)}
                </div>
              </Item>
              <Item>
                <div>
                  <TravelExploreOutlinedIcon />
                  <FormattedMessage
                    id="messages.customerOrders.details.tracking"
                    defaultMessage="Tracking Number"
                  />
                </div>
                <div>{order?.trackingNumber || 'n/a'}</div>
              </Item>
              <Item>
                <div>
                  <CalendarTodayIcon />
                  <FormattedMessage
                    id="messages.customerOrders.details.delivery_exp"
                    defaultMessage="EXPECTED DELIVERY"
                  />
                </div>
                <div>
                  {renderDate(order?.estimatedDeliveryTime?.date, order?.store?.timezone?.name) ||
                    'n/a'}
                </div>
              </Item>
              <Item>
                <div>
                  <LocalShippingIcon />
                  <FormattedMessage
                    id="messages.customerOrders.details.delivery_by"
                    defaultMessage="Delivered By"
                  />
                </div>
                <div>{order?.deliveredBy || 'n/a'}</div>
              </Item>
              <Item>
                <div>
                  <CurrencyExchangeOutlinedIcon />
                  <FormattedMessage
                    id="messages.customerOrders.details.refundReason"
                    defaultMessage="Refund Reason"
                  />
                </div>
                <div>{order?.refundReason || 'n/a'}</div>
              </Item>
              {Object.keys(order?.additionalInformation ?? {}).map((group, i) => (
                <React.Fragment key={`order-additional-group-${i}`}>
                  <ItemClick onClick={handleOpenPopupInfo(group)}>
                    <div>
                      {panels?.[group] === true ? (
                        <ExpandMoreOutlinedIcon />
                      ) : (
                        <KeyboardArrowRightOutlinedIcon />
                      )}
                      <FormattedMessage
                        id={'messages.customerOrders.details.' + group}
                        defaultMessage={toTitleCase(group)}
                      />
                    </div>
                    <div></div>
                  </ItemClick>
                  {panels?.[group] === true
                    ? Array.isArray(order?.additionalInformation?.[group])
                      ? order?.additionalInformation?.[group].map((objects, k) =>
                          Object.keys(objects).map((field, j) => (
                            <SubItem key={`order-additional-group-${i}-${j}-${k}`}>
                              <div>{toTitleCase(field)}</div>
                              <div>
                                {objects?.[field] && typeof objects?.[field] === 'object' ? (
                                  <pre>
                                    {JSON.stringify(objects?.[field], null, 1)
                                      .replaceAll(/[[]{}",]/g, '')
                                      .replaceAll(/^[\n\s]*/g, '  ')
                                      .replaceAll(/[\n\s]*$/g, '')
                                      .replaceAll(/\s\n\s\n/g, '\n')
                                      .replaceAll(/^\s+\n*$/gm, '')}
                                  </pre>
                                ) : (
                                  <div>
                                    {objects?.[field]
                                      ? objects?.[field]
                                      : objects?.[field] === 0
                                      ? 0
                                      : 'n/a'}
                                  </div>
                                )}
                              </div>
                            </SubItem>
                          )),
                        )
                      : Object.keys(order?.additionalInformation?.[group] ?? {}).map((field, j) => (
                          <SubItem key={`order-additional-group-${i}-${j}`}>
                            <div>{toTitleCase(field)}</div>
                            <div>
                              {order?.additionalInformation?.[group]?.[field]
                                ? JSON.stringify(
                                    order?.additionalInformation?.[group]?.[field],
                                    null,
                                    2,
                                  ).replaceAll(/[{}",]/g, '')
                                : order?.additionalInformation?.[group]?.[field] === 0
                                ? 0
                                : 'n/a'}
                            </div>
                          </SubItem>
                        ))
                    : null}
                </React.Fragment>
              ))}
              <Buttons>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => handleSend(order?._id.$oid)}
                >
                  <FormattedMessage
                    id="messages.customerOrders.details.button.send"
                    defaultMessage="Send"
                  />
                </Button>
              </Buttons>
            </OrderDetails>
          </StyledAccordionItemPanel>
        </AccordionItem>
      </StyledAccordion>
    );
  };

  return (
    <Container>
      {isLoading && page === 1 ? (
        <div style={{ textAlign: 'center', marginTop: '10px' }}>
          <Loading>Loading orders...</Loading>
        </div>
      ) : orders.length > 0 ? (
        orders.map((order, index) => renderOrder(order, index))
      ) : (
        <h4>
          <FormattedMessage
            id="messages.customerOrders.title.no_orders"
            defaultMessage="No Orders"
          />
        </h4>
      )}

      {hasMore && page < 10 && (
        <div style={{ textAlign: 'center', marginTop: '20px' }}>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => loadOrders(page + 1)}
          >
            Load more orders
          </Button>
        </div>
      )}
    </Container>
  );
};

export default React.memo(CustomerOrders);
